/**
 * Code Analysis Executor
 *
 * Handles the 'code-analysis' and 'pipeline-code-analysis' node execution.
 * Calls the ModernizeIT API Layer /codeanalysis endpoint.
 *
 * API Flow (WORKER MODE):
 * 1. Verify upstream ingest job is 'completed' (poll if needed)
 * 2. POST /codeanalysis → returns job_id with status QUEUED
 * 3. Poll GET /jobs/{job_id} → wait until status = 'completed'
 * 4. Extract results from job.output_data
 *
 * CRITICAL: Must wait for upstream ingest to complete before starting.
 */

import { pollJobUntilComplete } from '../../utils/jobPoller.js'
import { getAuthHeaders, handleApiError } from './authHelper.js'

/**
 * Execute the code analysis node
 *
 * @param {Object} config - Node configuration
 * @param {Object} context - Workflow execution context
 * @param {Function} onProgress - Progress callback (progress: 0-100, message: string)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Execution result with job info and artifacts
 */
export async function execute(config, context, onProgress = () => {}, signal = null) {
  const apiBaseUrl = context.api_base_url

  onProgress(0, 'Preparing code analysis...')

  // Check for abort before starting
  if (signal?.aborted) {
    throw new Error('Execution cancelled')
  }

  // 1. Verify upstream ingest job exists
  const ingestJobId = context.jobs?.ingest_job_id
  if (!ingestJobId) {
    throw new Error('Code Analysis requires ingest job ID from Upload node. Run Upload first.')
  }

  // 2. Verify upstream ingest job is completed (poll if needed)
  console.log(`[CodeAnalysisExecutor] Verifying upstream ingest job ${ingestJobId} is completed...`)
  onProgress(2, 'Waiting for Upload to complete...')

  try {
    // Poll the ingest job until it's completed
    await pollJobUntilComplete(ingestJobId, apiBaseUrl, {
      maxWaitSeconds: 600,
      pollInterval: 1000,
      onProgress: (progress, message) => {
        // Map upstream polling progress from 2-8%
        const mappedProgress = 2 + (progress * 0.06)
        onProgress(mappedProgress, `Waiting for Upload: ${message}`)
      },
      signal
    })
    console.log(`[CodeAnalysisExecutor] Upstream ingest job ${ingestJobId} is completed`)
  } catch (error) {
    throw new Error(`Upstream Upload job failed: ${error.message}`)
  }

  onProgress(8, 'Upload complete, starting code analysis...')

  // 3. Build the request body
  const requestBody = {
    scout_account_id: context.scout_account_id,
    application_name: context.application_name,
    user_id: context.user_id, // REQUIRED 2026-01-14: For AWS credentials lookup
    ingest_job_id: ingestJobId, // Verified completed upstream job
    // Main program can be specified or auto-detected
    main_program: config.main_program || null,
    // Generation options
    generate_java: config.generate_java !== false, // Default true
    generate_graphs: config.generate_graphs !== false, // Default true
  }

  onProgress(10, 'Submitting analysis job...')

  // 4. Submit the job
  let jobResponse
  try {
    const response = await fetch(`${apiBaseUrl}/codeanalysis`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(requestBody),
      signal,
    })

    if (!response.ok) {
      await handleApiError(response)
    }

    jobResponse = await response.json()

    // Check for API-level error in response
    if (jobResponse.success === false) {
      throw new Error(jobResponse.error || 'Code analysis failed')
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    if (error.message.includes('Failed to fetch')) {
      throw new Error(`Cannot connect to API at ${apiBaseUrl}. Is the server running?`)
    }
    throw error
  }

  const jobId = jobResponse.job_id
  if (!jobId) {
    throw new Error('API did not return a job_id')
  }

  console.log(`[CodeAnalysisExecutor] Job created: ${jobId}, polling until completed...`)
  onProgress(15, `Job started: ${jobId}`)

  // 5. Poll for completion using generic jobs endpoint
  const completedJob = await pollJobUntilComplete(jobId, apiBaseUrl, {
    maxWaitSeconds: 600,
    pollInterval: 1000,
    onProgress: (progress, message) => {
      // Map polling progress from 15-95%
      const mappedProgress = 15 + (progress * 0.80)
      onProgress(mappedProgress, message)
    },
    signal
  })

  console.log('[CodeAnalysisExecutor] Job completed, extracting results from output_data')
  onProgress(95, 'Extracting results...')

  // 6. Extract results from job.output_data (worker mode)
  const outputData = completedJob.output_data
  if (!outputData) {
    throw new Error('Job completed but has no output_data')
  }

  onProgress(100, 'Code analysis complete')

  // 7. Return formatted result
  return formatResult(jobId, outputData, context.jobs)
}

/**
 * Format the result object for consistent structure
 */
function formatResult(jobId, data, contextJobs = {}) {
  // 2026-01-28: Debug logging for job chain preservation
  console.log('[CodeAnalysisExecutor] formatResult called with contextJobs:', contextJobs)

  const resultJobs = {
    ...contextJobs,  // Preserve job chain from upstream nodes
    codeanalysis_job_id: jobId,
    code_analysis_job_id: jobId,  // ALSO set this for Service Generation compatibility
  }

  console.log('[CodeAnalysisExecutor] Returning jobs:', resultJobs)

  return {
    // Job tracking
    // 2026-01-27: Preserve upstream job IDs (ingest_job_id, etc.) for downstream nodes
    jobs: resultJobs,

    // Status
    success: data.success !== false,
    status: data.status || 'completed',

    // Paths
    artifacts_path: data.artifacts_path,

    // Main program info
    main_program: data.main_program,
    base_name: data.base_name,

    // Execution metrics
    duration_ms: data.duration_ms,

    // Summary statistics
    summary: data.summary || {},

    // Artifacts produced
    artifacts: {
      json: data.artifacts?.json || data.json_artifacts || [],
      graphs: data.artifacts?.graphs || data.graphs || [],
      java_project: data.artifacts?.java_project || data.java_project || null,
    },

    // Error info if any
    error: data.error || null,
  }
}

export default {
  execute,
}
